import React, { useEffect, useState } from 'react';
import { trackPublicCTA } from '../../utils/publicFunnelTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DemoFormModal({ isOpen, onClose, t, language, prefillData = null, source = 'landing_page_demo_form' }) {
  const [formData, setFormData] = useState({ name: '', school: '', phone: '', email: '', students: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [demoCredentials, setDemoCredentials] = useState(null);

  useEffect(() => {
    if (isOpen && prefillData) {
      setFormData((prev) => ({
        ...prev,
        name: prefillData.name || prev.name,
        school: prefillData.school || prev.school,
        phone: prefillData.phone || prev.phone,
        email: prefillData.email || prev.email,
        students: prefillData.students || prev.students,
        message: prefillData.message || prev.message,
      }));
    }
  }, [isOpen, prefillData]);

  useEffect(() => {
    if (isOpen) {
      trackPublicCTA({
        source_page: source,
        cta_id: `${source}_open`,
        cta_label: 'Open Demo Form',
        destination: 'demo_modal'
      });
    }
  }, [isOpen, source]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Call demo booking API — creates credentials + sends WhatsApp
      const res = await fetch(`${API_URL}/api/demo/book`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ 
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          school_name: formData.school,
          city: '',
          source: prefillData?.chatSessionId ? 'aayush_demo_form' : source,
          chat_session_id: prefillData?.chatSessionId || null
        }) 
      });
      const data = await res.json();
      if (data.success) {
        setDemoCredentials(data);
        trackPublicCTA({
          source_page: source,
          cta_id: `${source}_submit`,
          cta_label: 'Submit Demo Form',
          destination: 'demo_booked',
          session_id: prefillData?.chatSessionId || null,
          phone: formData.phone,
          email: formData.email,
          meta: { school: formData.school }
        });
        // Track conversion events
        if (window.gtag) window.gtag('event', 'demo_booked', { event_category: 'lead', school_name: formData.school });
        if (window.fbq) window.fbq('track', 'Lead', { content_name: 'demo_booking', content_category: 'school_lead' });
      }

      // Also save to leads endpoint (legacy)
      try {
        await fetch(`${API_URL}/api/leads/demo-request`, { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' }, 
          body: JSON.stringify({ ...formData, language, source: 'landing_page' }) 
        });
      } catch (err) {}
    } catch (err) { 
      console.error('Demo booking error:', err); 
    }
    setFormSubmitted(true);
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    setFormSubmitted(false);
    setDemoCredentials(null);
    setFormData({ name: '', school: '', phone: '', email: '', students: '', message: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="demo-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">{formSubmitted ? (language === 'hi' ? 'Demo Ready!' : 'Demo Ready!') : t.form.title}</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="demo-form-close">&times;</button>
          </div>
          
          {formSubmitted ? (
            <div className="text-center py-4 space-y-4" data-testid="demo-form-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {demoCredentials ? (
                <>
                  <p className="text-gray-600 text-sm">
                    {demoCredentials.whatsapp_sent 
                      ? (language === 'hi' ? 'WhatsApp par credentials bhej diye hain!' : 'Credentials sent on WhatsApp!')
                      : (language === 'hi' ? 'Neeche apne demo credentials hain:' : 'Your demo credentials:')}
                  </p>
                  
                  <div className="bg-orange-50 rounded-xl p-4 border-2 border-dashed border-orange-300 text-left" data-testid="demo-credentials-card">
                    <p className="text-xs text-orange-600 mb-2 font-bold uppercase">Login Credentials</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="font-bold text-gray-800 text-lg" data-testid="demo-cred-phone">{demoCredentials.demo_phone}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Password:</span>
                        <span className="font-bold text-gray-800 text-lg" data-testid="demo-cred-password">{demoCredentials.demo_password}</span>
                      </div>
                    </div>
                  </div>
                  
                  <a
                    href="/login"
                    className="block w-full py-3.5 bg-orange-500 text-white rounded-xl font-bold text-center hover:bg-orange-600 transition text-lg"
                    data-testid="demo-login-cta"
                  >
                    {language === 'hi' ? 'Abhi Login Karein →' : 'Login Now →'}
                  </a>
                </>
              ) : (
                <>
                  <p className="text-lg text-gray-700">{t.form.success}</p>
                  <button onClick={handleClose} className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition">OK</button>
                </>
              )}
              
              <button onClick={handleClose} className="text-sm text-gray-400 hover:text-gray-600">
                {language === 'hi' ? 'Band karein' : 'Close'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="demo-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.form.fields.name} *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-testid="demo-input-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.form.fields.school} *</label>
                <input type="text" required value={formData.school} onChange={(e) => setFormData({ ...formData, school: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-testid="demo-input-school" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.form.fields.phone} *</label>
                <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="9876543210" data-testid="demo-input-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-testid="demo-input-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.form.fields.students} *</label>
                <select required value={formData.students} onChange={(e) => setFormData({ ...formData, students: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" data-testid="demo-input-students">
                  <option value="">Select</option>
                  <option value="<100">&lt; 100</option>
                  <option value="100-200">100 - 200</option>
                  <option value="200-500">200 - 500</option>
                  <option value="500-1000">500 - 1000</option>
                  <option value="1000+">1000+</option>
                </select>
              </div>
              <button type="submit" disabled={submitting} className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50" data-testid="demo-submit-btn">
                {submitting ? '...' : (language === 'hi' ? 'WhatsApp par Demo Credentials Bhejein' : 'Send Demo Credentials on WhatsApp')}
              </button>
              <p className="text-center text-xs text-gray-400">
                {language === 'hi' ? 'Aapka data safe hai. Spam nahi karte.' : 'Your data is safe. We never spam.'}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
